import type { AppState, BackupEnvelope } from '../types'
import { initialState } from './seed'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const numberOr = (value: unknown, fallback: number): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const stringOr = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback

const booleanOr = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : fallback

const sprintStatuses = new Set(['Not Started', 'In Progress', 'Blocked', 'Complete'])

function normalizeSprint(input: unknown, base: AppState): AppState['sprint'] {
  if (!Array.isArray(input)) return base.sprint
  const byDay = new Map<number, Record<string, unknown>>()

  input.forEach((item, index) => {
    if (!isRecord(item)) return
    const rawDay = Number(item.day)
    const day = Number.isInteger(rawDay) && rawDay >= 1 && rawDay <= base.sprint.length ? rawDay : index + 1
    if (day >= 1 && day <= base.sprint.length && !byDay.has(day)) byDay.set(day, item)
  })

  return base.sprint.map((baseItem) => {
    const item = byDay.get(baseItem.day) ?? {}
    const status = typeof item.status === 'string' && sprintStatuses.has(item.status) ? item.status : baseItem.status
    return {
      ...baseItem,
      id: stringOr(item.id, baseItem.id),
      status: status as AppState['sprint'][number]['status'],
      evidence: stringOr(item.evidence, '')
    }
  })
}

export function normalizeState(input: unknown): AppState {
  const base = initialState()
  if (!isRecord(input)) return base
  const raw = input
  const settings = isRecord(raw.settings) ? raw.settings : {}
  const manualChecks = isRecord(raw.manualChecks) ? raw.manualChecks : {}

  return {
    ...base,
    settings: {
      material: numberOr(settings.material, base.settings.material),
      packaging: numberOr(settings.packaging, base.settings.packaging),
      processingRate: numberOr(settings.processingRate, base.settings.processingRate),
      processingFixed: numberOr(settings.processingFixed, base.settings.processingFixed),
      electricSession: numberOr(settings.electricSession, base.settings.electricSession),
      unitsSession: Math.max(1, numberOr(settings.unitsSession, base.settings.unitsSession)),
      failureReserve: numberOr(settings.failureReserve, base.settings.failureReserve),
      laborHour: numberOr(settings.laborHour, base.settings.laborHour),
      targetMargin: Math.min(0.95, Math.max(0.01, numberOr(settings.targetMargin, base.settings.targetMargin))),
      taxReserve: numberOr(settings.taxReserve, base.settings.taxReserve),
      emergencyReserve: numberOr(settings.emergencyReserve, base.settings.emergencyReserve)
    },
    sprint: normalizeSprint(raw.sprint, base),
    capacity: Array.isArray(raw.capacity) ? raw.capacity.map((item) => normalizeRecord(item)) as unknown as AppState['capacity'] : base.capacity,
    offers: Array.isArray(raw.offers) ? raw.offers.map((item) => normalizeRecord(item)) as unknown as AppState['offers'] : base.offers,
    leads: Array.isArray(raw.leads) ? raw.leads.map((item, index) => ({ rank: index + 1, ...normalizeRecord(item) })) as AppState['leads'] : base.leads,
    orders: Array.isArray(raw.orders) ? raw.orders.map((item) => normalizeRecord(item)) as unknown as AppState['orders'] : base.orders,
    content: Array.isArray(raw.content) ? raw.content.map((item) => normalizeRecord(item)) as unknown as AppState['content'] : base.content,
    decisions: Array.isArray(raw.decisions) ? raw.decisions.map((item) => normalizeRecord(item)) as unknown as AppState['decisions'] : base.decisions,
    manualChecks: {
      samples: booleanOr(manualChecks.samples, false),
      offers: booleanOr(manualChecks.offers, false)
    },
    daily: Array.isArray(raw.daily) ? raw.daily.map((item) => normalizeRecord(item)) as unknown as AppState['daily'] : base.daily
  }
}

function normalizeRecord(input: unknown): Record<string, unknown> {
  if (!isRecord(input)) return {}
  const output: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(input)) {
    if (typeof value === 'number') output[key] = Number.isFinite(value) ? value : 0
    else if (typeof value === 'boolean' || typeof value === 'string') output[key] = value
    else if (value === null || value === undefined) output[key] = ''
    else output[key] = value
  }
  if (!stringOr(output.id)) output.id = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
  return output
}

export function parseBackup(raw: string): BackupEnvelope {
  const parsed = JSON.parse(raw) as unknown
  if (isRecord(parsed) && parsed.app === 'trevahhh-os' && isRecord(parsed.state)) {
    return {
      app: 'trevahhh-os',
      version: numberOr(parsed.version, 1),
      exportedAt: stringOr(parsed.exportedAt, new Date().toISOString()),
      state: normalizeState(parsed.state)
    }
  }
  return {
    app: 'trevahhh-os',
    version: 1,
    exportedAt: new Date().toISOString(),
    state: normalizeState(parsed)
  }
}

export function validateBackupEnvelope(envelope: BackupEnvelope): string[] {
  const issues: string[] = []
  if (envelope.app !== 'trevahhh-os') issues.push('Backup app marker is invalid.')
  if (!envelope.exportedAt) issues.push('Backup is missing exportedAt.')
  if (!Array.isArray(envelope.state.leads)) issues.push('Leads collection is invalid.')
  if (!Array.isArray(envelope.state.capacity)) issues.push('Capacity collection is invalid.')
  if (!envelope.state.settings) issues.push('Settings are missing.')
  return issues
}
